import { playErrorFeedback, playNotReadyFeedback, showsFailImage } from '../utils/scanFeedback';
import * as RootNavigation from '../components/root_navigation';
import { AddBox, PrintSlip } from './orderActions';
import { removeFromBox } from '../services/scanpackApiService';
import {
  isMultiBoxEnabled,
  getActiveBoxId,
  getActiveBoxName,
  canCreateNewBox,
  getBoxItemCountFromOrder,
  getFailTimeout,
  replaceBoxTempId
} from '../utils/orderHelpers';
import {
  setActiveBoxIndex,
  setMultiBoxAlert,
  showPrintSlipFailedNotice,
  setScanPackBarcodeInput,
  setScanPackScanError,
  setScanPackCurrentOrder,
  setCurrentItem,
  getStateValues
} from './scanpackScanningSetters';
import { SET_SCAN_PACK_LOADING } from '../constants';
import { getGuidedView } from '../components/ScanpackScanning/utils/boxChampGuidedSelectors';
import { refuseManualBoxNavigation } from './boxChampGuidedNotices';
import { queueReprintPrompt } from '../components/reprint/reprintUtils';
import { getPostScanSteps } from '../utils/postScanning/postScanConfig';

export const ensureFirstBox = (dispatch, getState, currentOrder, generalSettings) => {
  if (!isMultiBoxEnabled(generalSettings)) return { order: currentOrder, boxName: 'BOX 1' };
  const boxes = currentOrder?.boxes || [];
  if (boxes.length > 0) return { order: currentOrder, boxName: null };

  const tempId = `temp-${Date.now()}`;
  const boxName = 'Box 1';
  const updatedOrder = {
    ...currentOrder,
    boxes: [{ id: tempId, name: boxName }]
  };

  const orderId = currentOrder?.id;
  if (orderId) {
    dispatch(AddBox(
      { name: boxName, order_id: orderId },
      (data) => {
        const realId = data?.box?.id;
        if (realId) {
          const latest = getState().scanPackScanning.currentOrder;
          if (latest) {
            dispatch(setScanPackCurrentOrder(replaceBoxTempId(latest, tempId, realId)));
          }
        }
      }
    ));
  }

  return { order: updatedOrder, boxName };
};

export const resolveMultiBoxContext = (dispatch, getState, currentOrder, generalSettings, activeBoxIndex) => {
  const multiBox = isMultiBoxEnabled(generalSettings);
  const guidedView = getGuidedView(getState());
  if (guidedView) {
    const active = guidedView.projection.boxes.find(
      entry => entry.box.id === guidedView.projection.activeBoxId
    );
    return {
      multiBox,
      orderForScan: currentOrder,
      localBoxId: null,
      boxName: active?.box?.name || getActiveBoxName(currentOrder, activeBoxIndex)
    };
  }
  const { order: orderWithBox, boxName: firstBoxName } = ensureFirstBox(dispatch, getState, currentOrder, generalSettings);
  const orderForScan = firstBoxName ? orderWithBox : currentOrder;
  const localBoxId = multiBox ? getActiveBoxId(orderForScan, activeBoxIndex) : null;
  const boxName = firstBoxName || getActiveBoxName(orderForScan, activeBoxIndex);
  return { multiBox, orderForScan, localBoxId, boxName };
};

export const printSlipWithFeedback = (dispatch, getState, payload) =>
  dispatch(PrintSlip(payload)).then(result => {
    if (result.ok) return;
    playNotReadyFeedback(getState);
    dispatch(showPrintSlipFailedNotice());
  });

const requestNewBox = (dispatch, name, orderId) =>
  new Promise(resolve => {
    dispatch(AddBox(
      { name, order_id: orderId },
      data => resolve({ ok: true, data }),
      undefined,
      error => resolve({ ok: false, error })
    ));
  });

export function handleBoxNavigation(direction) {
  return async (dispatch, getState) => {
    if (dispatch(refuseManualBoxNavigation())) return;

    const { currentOrder, generalSettings, scanPackSettings, activeBoxIndex } = getStateValues(getState);

    if (!isMultiBoxEnabled(generalSettings)) {
      playNotReadyFeedback(getState);
      dispatch(setScanPackBarcodeInput(''));
      return;
    }

    const boxes = currentOrder?.boxes || [];
    dispatch(setScanPackBarcodeInput(''));

    if (direction === 'prev') {
      if (activeBoxIndex <= 0) {
        playNotReadyFeedback(getState);
        return;
      }
      dispatch(setActiveBoxIndex(activeBoxIndex - 1));
      return;
    }

    const isOnLastBox = activeBoxIndex >= boxes.length - 1;

    if (!isOnLastBox) {
      dispatch(setActiveBoxIndex(activeBoxIndex + 1));
      return;
    }

    if (!canCreateNewBox(currentOrder)) {
      dispatch(setMultiBoxAlert(true));
      playNotReadyFeedback(getState);
      setTimeout(() => {
        dispatch(setMultiBoxAlert(false));
      }, 2500);
      return;
    }

    const orderId = currentOrder?.id;
    if (!orderId) return;

    const newBoxName = `Box ${boxes.length + 1}`;
    dispatch({ type: SET_SCAN_PACK_LOADING, payload: true });

    try {
      const result = await requestNewBox(dispatch, newBoxName, orderId);
      if (!result.ok) {
        playErrorFeedback(getState);
        if (showsFailImage(scanPackSettings)) dispatch(setScanPackScanError(true, getFailTimeout(scanPackSettings)));
        return;
      }
      const data = result.data;
      const realId = data?.box?.id;
      const latest = getState().scanPackScanning.currentOrder;
      const latestBoxes = latest?.boxes || [];
      const updatedOrder = {
        ...latest,
        boxes: [...latestBoxes, { id: realId, name: newBoxName }]
      };
      dispatch(setScanPackCurrentOrder(updatedOrder));
      dispatch(setActiveBoxIndex(latestBoxes.length));

      const perBoxPacking = generalSettings?.per_box_packing_slips;
      if (perBoxPacking === 'when_new_boxes_are_started') {
        const currentBoxId = boxes[activeBoxIndex]?.id;
        printSlipWithFeedback(dispatch, getState, {
          box_ids: currentBoxId,
          orderArray: [{ id: orderId }]
        });
        const scanPackSettings = getState().user?.bothSettings?.data?.data?.scanpack_setting;
        await queueReprintPrompt(currentOrder?.increment_id, 'packing_slip', scanPackSettings, generalSettings);
      }

      const perBoxShipping = generalSettings?.per_box_shipping_label_creation;
      const hasLabelData = Object.keys(data?.ss_label_data || {}).length > 0;
      if (perBoxShipping === 'per_box_shipping_label_creation_after_box' && hasLabelData) {
        const bothSettings = getState().user?.bothSettings?.data?.data;
        setTimeout(() => {
          RootNavigation.navigate('ShippingLabel', {
            time: new Date(),
            setting: bothSettings,
            userRole: getState().user?.userInfo,
            basicinfo: currentOrder,
            directPrintingSetting: generalSettings?.print_ss_shipping_labels,
            isolatedComponent: true,
            returnToScanPack: true,
            multiBoxMode: true,
            currentOrderId: currentOrder?.increment_id
          });
        }, 300);
      }
    } finally {
      dispatch({ type: SET_SCAN_PACK_LOADING, payload: false });
    }
  };
}

export const printCompletedOrderSlips = (dispatch, getState, currentOrder, generalSettings) => {
  if (!isMultiBoxEnabled(generalSettings)) return;
  if (getPostScanSteps(getState()).steps.includes('PackingSlip')) return;

  const filledBoxIds = (currentOrder?.boxes || [])
    .map(box => box.id)
    .filter(boxId => getBoxItemCountFromOrder(currentOrder, boxId) > 0);
  if (filledBoxIds.length === 0) return;

  const perBoxPacking = generalSettings?.per_box_packing_slips;
  let boxIds;
  if (perBoxPacking === 'when_new_boxes_are_started') {
    const lastBoxId = currentOrder.boxes[currentOrder.boxes.length - 1].id;
    if (!filledBoxIds.includes(lastBoxId)) return;
    boxIds = lastBoxId;
  } else if (perBoxPacking === 'when_order_is_complete') {
    boxIds = filledBoxIds;
  } else {
    return;
  }
  printSlipWithFeedback(dispatch, getState, { box_ids: boxIds, orderArray: [{ id: currentOrder.id }] });
};

export function removeBoxItem(boxId, orderItemId, productId, kitProductId) {
  return async (dispatch, getState) => {
    const { currentOrder } = getState().scanPackScanning;

    const snapshot = currentOrder;
    dispatch({ type: SET_SCAN_PACK_LOADING, payload: true });

    try {
      const res = await removeFromBox(boxId, orderItemId, kitProductId);
      if (res.data?.status) {
        const latest = getState().scanPackScanning.currentOrder;
        dispatch(setScanPackCurrentOrder({
          ...latest,
          scanned_items: res.data.scanned_items,
          unscanned_items: res.data.unscanned_items,
          order_item_boxes: res.data.order_item_boxes,
          boxes: res.data.box
        }));
        dispatch(setCurrentItem());
      }
    } catch {
      dispatch(setScanPackCurrentOrder(snapshot));
      dispatch(setCurrentItem());
    } finally {
      dispatch({ type: SET_SCAN_PACK_LOADING, payload: false });
    }
  };
}

export const handleMultiBoxShippingLabel = (getState, currentOrder, generalSettings, scanPackSettings, navDelay) => {
  const boxes = currentOrder?.boxes || [];
  if (!generalSettings?.multi_box_shipments || boxes.length === 0) return false;
  if (!currentOrder?.scanned_items?.some(item => item.scanned_qty > 0)) return false;

  const perBoxSetting = generalSettings?.per_box_shipping_label_creation;

  if (
    perBoxSetting === 'per_box_shipping_label_creation_after_box' &&
    currentOrder?.unscanned_items?.length === 0
  ) {
    const hasPostScanVerification = scanPackSettings?.post_scanning_option &&
                                     scanPackSettings?.post_scanning_option !== 'None';
    if (hasPostScanVerification) return false;

    const bothSettings = getState().user?.bothSettings?.data?.data;
    setTimeout(() => {
      RootNavigation.navigate('ShippingLabel', {
        time: new Date(),
        setting: bothSettings,
        userRole: getState().user?.userInfo,
        basicinfo: currentOrder,
        directPrintingSetting: generalSettings?.print_ss_shipping_labels,
        isolatedComponent: true,
        returnToScanPack: true,
        multiBoxMode: true,
        isFinalBox: true,
        currentOrderId: currentOrder?.increment_id,
        presentationStyle: 'modal'
      });
    }, navDelay);
    return true;
  }

  if (perBoxSetting === 'per_box_shipping_label_creation_after_order') {
    const bothSettings = getState().user?.bothSettings?.data?.data;
    setTimeout(() => {
      RootNavigation.navigate('ShippingLabel', {
        time: new Date(),
        setting: bothSettings,
        userRole: getState().user?.userInfo,
        basicinfo: currentOrder,
        directPrintingSetting: generalSettings?.print_ss_shipping_labels,
        isolatedComponent: true,
        returnToScanPack: true,
        multiBoxMode: true,
        isFinalBox: true,
        currentOrderId: currentOrder?.increment_id,
        presentationStyle: 'modal'
      });
    }, navDelay);
    return true;
  }

  return false;
};
